import React, { useState } from 'react';
import { FiTrash2, FiX, FiAlertTriangle } from 'react-icons/fi';

interface Case {
  id: string;
  caseId: string;
  clientName: string;
  caseType: string;
}

interface CaseDeleteModalProps {
  isOpen: boolean;
  case: Case | null;
  onConfirm: (caseId: string) => Promise<void>;
  onCancel: () => void;
  documents: number;
  forms: number;
}

const CaseDeleteModal: React.FC<CaseDeleteModalProps> = ({
  isOpen,
  case: caseToDelete,
  onConfirm,
  onCancel,
  documents,
  forms
}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState(0);

  if (!isOpen || !caseToDelete) return null;

  const handleDelete = async () => {
    setIsDeleting(true);
    setDeleteProgress(0);

    try {
      // Simple progress animation
      const animateProgress = () => {
        let progress = 0;
        const interval = setInterval(() => {
          progress += 10;
          setDeleteProgress(progress);
          if (progress >= 90) {
            clearInterval(interval);
          }
        }, 100);
        return interval;
      };

      const progressInterval = animateProgress();
      
      // Wait a bit for the animation, then complete
      await new Promise(resolve => setTimeout(resolve, 1000));
      clearInterval(progressInterval);
      setDeleteProgress(100);
      
      await onConfirm(caseToDelete.id);
    } catch (error) {
      console.error('Error deleting case:', error);
      alert('Failed to delete case. Please try again.');
    } finally {
      setIsDeleting(false);
      setDeleteProgress(0);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content delete-modal">
        <div className="modal-header">
          <h3>
            ⚠️
            Delete Case
          </h3>
          <button 
            className="close-btn"
            onClick={onCancel}
            disabled={isDeleting}
          >
            ✕
          </button>
        </div>
        
        <div className="modal-body">
          <p><strong>Delete this case?</strong></p>
          
          <div className="case-info">
            <p><strong>Case ID:</strong> {caseToDelete.caseId}</p>
            <p><strong>Client:</strong> {caseToDelete.clientName}</p>
            <p><strong>Type:</strong> {caseToDelete.caseType}</p>
          </div>
          
          <div className="warning-box">
            <p>
              ⚠️
              <strong>This action is irreversible!</strong>
            </p>
            <p>Case and all contents will be permanently deleted.</p>
          </div>
          
          <div className="content-summary">
            <p><strong>Contents:</strong> {documents} documents, {forms} forms</p>
          </div>

          {/* Progress Bar Section */}
          {isDeleting && (
            <div className="delete-progress">
              <div className="progress-header">
                <h4>Deleting Case...</h4>
                <span className="progress-percentage">{deleteProgress}%</span>
              </div>
              
              <div className="progress-bar-container">
                <div 
                  className="progress-bar"
                  style={{ width: `${deleteProgress}%` }}
                ></div>
              </div>
            </div>
          )}
        </div>
        
        <div className="modal-footer">
          <button 
            className="cancel-btn"
            onClick={onCancel}
            disabled={isDeleting}
          >
            Cancel
          </button>
          <button 
            className="delete-btn"
            onClick={handleDelete}
            disabled={isDeleting}
            style={{
              backgroundColor: isDeleting ? '#6b7280' : '#dc3545',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '4px',
              cursor: isDeleting ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            {isDeleting ? (
              <>
                <div className="spinner"></div>
                Deleting...
              </>
            ) : (
              <>
                🗑️
                Delete Case
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CaseDeleteModal;
